import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Handshake, Search, Send, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { useDebouncedValue } from '@/lib/use-debounce';
import { Button } from '@/components/ui/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { NAV_ITEMS } from '@/app/layout/nav';
import { searchHitDetail, searchHitLabel, type SearchResponse } from '@/app/layout/search';

const GROUPS: Array<{ key: keyof SearchResponse; label: string; path: string; icon: typeof Briefcase }> = [
  { key: 'positions', label: 'Positions', path: '/positions', icon: Briefcase },
  { key: 'candidates', label: 'Candidates', path: '/candidates', icon: Users },
  { key: 'submissions', label: 'Submissions', path: '/submissions', icon: Send },
  { key: 'partners', label: 'Partners', path: '/partners', icon: Handshake },
];

/** Search button for the top bar + the ⌘K / Ctrl-K command palette. */
export function GlobalSearch() {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const debounced = useDebouncedValue(q.trim(), 250);
  const navigate = useNavigate();

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const { data, isFetching } = useQuery({
    queryKey: queryKeys.search(debounced),
    queryFn: () => api.get<SearchResponse>('/search', { query: { q: debounced } }),
    enabled: open && debounced.length >= 2,
    staleTime: 10_000,
  });

  const go = (path: string) => {
    setOpen(false);
    setQ('');
    navigate(path);
  };

  const hasHits = GROUPS.some((g) => (data?.[g.key]?.length ?? 0) > 0);

  return (
    <>
      <Button
        variant="outline"
        className="h-9 w-9 justify-start px-0 text-muted-foreground sm:w-64 sm:px-3"
        onClick={() => setOpen(true)}
        aria-label="Search (Ctrl+K)"
        aria-keyshortcuts="Control+K Meta+K"
      >
        <Search aria-hidden="true" className="mx-auto sm:mx-0" />
        <span className="hidden flex-1 text-left text-sm font-normal sm:inline">Search…</span>
        <kbd className="pointer-events-none hidden rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline">
          ⌘K
        </kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false} title="Search">
        <CommandInput
          placeholder="Search positions, candidates, submissions, partners…"
          value={q}
          onValueChange={setQ}
        />
        <CommandList>
          {debounced.length >= 2 && !isFetching && !hasHits ? <CommandEmpty>No results.</CommandEmpty> : null}
          {isFetching ? <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div> : null}
          {GROUPS.map((g) => {
            const hits = data?.[g.key] ?? [];
            if (hits.length === 0) return null;
            return (
              <CommandGroup key={g.key} heading={g.label}>
                {hits.slice(0, 8).map((hit) => (
                  <CommandItem
                    key={`${g.key}-${hit.id}`}
                    value={`${g.key}-${hit.id}`}
                    onSelect={() => go(`${g.path}/${hit.id}`)}
                  >
                    <g.icon aria-hidden="true" />
                    <div className="min-w-0">
                      <div className="truncate">{searchHitLabel(hit)}</div>
                      {searchHitDetail(hit) ? (
                        <div className="truncate text-xs text-muted-foreground">{searchHitDetail(hit)}</div>
                      ) : null}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
          {hasHits ? <CommandSeparator /> : null}
          <CommandGroup heading="Go to">
            {NAV_ITEMS.map((item) => (
              <CommandItem key={item.to} value={`nav-${item.to}`} onSelect={() => go(item.to)}>
                <item.icon aria-hidden="true" />
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
